import { useState, useEffect } from 'react';
import { useBackendSettings } from './useBackendSettings';

const SESSION_DISMISSED_KEY = 'scribelate_onboarding_session_dismissed';

interface UseOnboardingOptions {
  projectCount?: number;
}

export const useOnboarding = (options?: UseOnboardingOptions) => {
  const { projectCount = 0 } = options || {};
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { settings, saveSettings, isLoading: isLoadingSettings } = useBackendSettings();

  useEffect(() => {
    // Wait for settings to load from backend before deciding whether to show onboarding
    if (isLoadingSettings) {
      return;
    }

    // Check if user has permanently completed onboarding (from backend)
    const hasCompletedOnboarding = settings?.onboarding_completed === 'true';
    
    // Check if user dismissed it this session (still use sessionStorage)
    const sessionDismissed = sessionStorage.getItem(SESSION_DISMISSED_KEY);
    
    // Show onboarding if:
    // 1. User has never completed it, OR
    // 2. User has zero projects (unless dismissed this session)
    if (!hasCompletedOnboarding || (projectCount === 0 && !sessionDismissed)) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [projectCount, settings, isLoadingSettings]);

  const completeOnboarding = () => {
    saveSettings({ onboarding_completed: 'true' });
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
    setShowOnboarding(false);
  };

  const skipOnboarding = () => {
    // Only set session storage, so it can show again if they still have 0 projects next session
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
    setShowOnboarding(false);
  };

  const resetOnboarding = () => {
    saveSettings({ onboarding_completed: 'false' });
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    setShowOnboarding(true);
  };

  return {
    showOnboarding,
    isLoading: isLoadingSettings,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
  };
};
